import * as React from "react";
import { useState } from "react";
import { TableColumnsType } from "antd";
import { AntDTable } from "@/common/components/ui";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/common/components/ui/card";
import { Input } from "@/common/components/ui/input";
import { Button } from "@/common/components/ui/button";
import { Badge } from "@/common/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/common/components/ui/tabs";
import { Label } from "@/common/components/ui/label";
import {
  Search,
  Download,
  Eye,
  Edit2,
  Trash2,
  Users,
  UserPlus,
  TrendingUp,
  IndianRupee,
  CheckCircle,
  Clock,
  XCircle,
  AlertTriangle,
  RefreshCw,
  User,
  MapPin,
  CreditCard,
  Wallet,
  UserCheck,
  Building2,
  ScrollText,
  ArrowLeft,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useQuery } from "@tanstack/react-query";
import NpsUserService from "@/services/FinEaseHomeAndAdmin/APIs/NpsUserService";
import { getTenantId } from "@/lib/storeManager";
import { SubmissionResult } from "@/utils/types/NpsUser";
import * as XLSX from "xlsx";
import { notify } from "@/common/components/common/Notification";

const AdminNPSUsers = () => {
  const { toast } = useToast();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedUser, setSelectedUser] = useState<SubmissionResult | null>(
    null
  );
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedRows, setSelectedRows] = useState<SubmissionResult[]>([]);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [viewMode, setViewMode] = useState<"list" | "detail">("list");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const tenantId = getTenantId() ?? "";

  const {
    data,
    isLoading,
    refetch: refetchUsers,
  } = useQuery({
    queryKey: ["npsUsers", tenantId, page, pageSize],
    queryFn: () => NpsUserService.listNpsUsers(page, pageSize),
    enabled: !!tenantId,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: 60_000,
  });

  console.log("refetchUsers", refetchUsers);
  console.log("showEditDialog", showEditDialog);

  const handleViewUser = (user: SubmissionResult) => {
    setSelectedUser(user);
    setViewMode("detail");
  };

  const handleBackToList = () => {
    setViewMode("list");
    setSelectedUser(null);
  };

  // Mock stats for now
  const stats = {
    totalUsers: data?.count ?? 0,
    activeUsers: 0,
    pendingUsers: 0,
    totalAUM: 0,
    totalContributions: 0,
    avgReturns: 0,
  };

  const handleDelete = (_id: string) => {
    toast({
      title: "User Deleted",
      description: "NPS user has been removed from the system.",
    });
  };

  const handleApproveKYC = (_id: string) => {
    toast({
      title: "KYC Approved",
      description: "User KYC has been approved successfully.",
    });
  };

  const handleRejectKYC = (_id: string) => {
    toast({
      title: "KYC Rejected",
      description: "User KYC has been rejected.",
    });
  };

  const onSelectChange = (keys: React.Key[], rows: SubmissionResult[]) => {
    setSelectedRowKeys(keys);
    setSelectedRows(rows);
  };

  const handleExport = () => {
    if (selectedRows.length === 0) {
      notify.error("No Data", "Please select users to export.");
      return;
    }

    const rows = selectedRows.map(item => ({
      ID: item.id,
      "First Name": item.subFstName ?? "-",
      Mobile: item.mobile ?? "-",
      PRAN: item.pranNumber ?? "-",
      "ACK ID": item.ackId ?? "-",
      "Receipt No": item.receiptNumber ?? "-",
      Status: item.status ?? "-",
      Date: item.created_on ?? "-",
    }));

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(rows);
    XLSX.utils.book_append_sheet(wb, ws, "NpsUsers");

    XLSX.writeFile(
      wb,
      `nps_users_${new Date().toISOString().split("T")[0]}.xlsx`
    );
    notify.success("Exported", `${rows.length} user(s) exported.`);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "Active":
        return (
          <Badge className="border-green-200 bg-green-100 text-green-700">
            Active
          </Badge>
        );
      case "Pending KYC":
        return (
          <Badge className="border-amber-200 bg-amber-100 text-amber-700">
            Pending KYC
          </Badge>
        );
      case "Inactive":
        return (
          <Badge className="border-red-200 bg-red-100 text-red-700">
            Inactive
          </Badge>
        );
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getRiskBadge = (risk: string) => {
    switch (risk) {
      case "High":
        return (
          <Badge className="border-red-200 bg-red-50 text-red-700">
            {risk}
          </Badge>
        );
      case "Medium":
        return (
          <Badge className="border-amber-200 bg-amber-50 text-amber-700">
            {risk}
          </Badge>
        );
      case "Low":
        return (
          <Badge className="border-green-200 bg-green-50 text-green-700">
            {risk}
          </Badge>
        );
      default:
        return <Badge variant="outline">{risk}</Badge>;
    }
  };

  const getKYCBadge = (status: string) => {
    switch (status) {
      case "Verified":
        return (
          <Badge className="bg-green-100 text-green-700">
            <CheckCircle className="mr-1 h-3 w-3" />
            Verified
          </Badge>
        );
      case "Pending":
        return (
          <Badge className="bg-amber-100 text-amber-700">
            <Clock className="mr-1 h-3 w-3" />
            Pending
          </Badge>
        );
      case "Under Review":
        return (
          <Badge className="bg-blue-100 text-blue-700">
            <RefreshCw className="mr-1 h-3 w-3" />
            Under Review
          </Badge>
        );
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  // Full Details View - Form Style
  if (viewMode === "detail" && selectedUser) {
    return (
      <div className="space-y-6">
        {/* Back Button & Header */}
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleBackToList}
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to List
          </Button>
          <div className="flex gap-3">
            <Button variant="outline" className="gap-2">
              <Download className="h-4 w-4" /> Export
            </Button>
            <Button variant="outline" className="gap-2">
              <Edit2 className="h-4 w-4" /> Edit
            </Button>
          </div>
        </div>

        {/* Main Form Card */}
        <Card className="border-border/50">
          <CardHeader className="border-b bg-gradient-to-r from-primary/5 to-transparent">
            <div className="flex flex-col justify-between gap-4 md:flex-row md:items-center">
              <div className="flex items-center gap-4">
                <div className="flex h-16 w-16 items-center justify-center rounded-full border-2 border-primary/20 bg-primary/10">
                  <User className="h-8 w-8 text-primary" />
                </div>
                <div>
                  <CardTitle className="font-display text-xl">
                    {selectedUser?.subFstName}
                  </CardTitle>
                  <CardDescription>
                    Application ID: NPS-
                    {(selectedUser?.id || "").toString().padStart(6, "0")}
                  </CardDescription>
                  <div className="mt-2 flex items-center gap-2">
                    {getStatusBadge(selectedUser?.status)}
                    {/* {getKYCBadge(selectedUser?.kycStatus)} */}
                  </div>
                </div>
              </div>
              <div className="flex gap-4 text-center">
                <div className="rounded-lg bg-muted/50 px-4 py-2">
                  <p className="text-xs text-muted-foreground">Corpus</p>
                  <p className="font-bold text-primary">
                    ₹{(0).toLocaleString()}
                  </p>
                </div>
                <div className="rounded-lg border border-green-100 bg-green-50 px-4 py-2">
                  <p className="text-xs text-muted-foreground">Returns</p>
                  <p className="font-bold text-green-600">+{0}%</p>
                </div>
              </div>
            </div>
          </CardHeader>

          <CardContent className="p-0">
            {/* Personal Details Section */}
            <div className="border-b p-6">
              <div className="mb-5 flex items-center gap-2">
                <User className="h-5 w-5 text-primary" />
                <h3 className="font-semibold text-secondary">
                  Personal Details
                </h3>
              </div>
              <div className="grid grid-cols-1 gap-x-8 gap-y-5 md:grid-cols-2 lg:grid-cols-4">
                <div>
                  <Label className="text-xs text-muted-foreground">
                    Full Name
                  </Label>
                  <p className="mt-1 font-medium">{selectedUser?.subFstName}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">
                    Date of Birth
                  </Label>
                  <p className="mt-1 font-medium">{selectedUser?.created_on}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">
                    Gender
                  </Label>
                  <p className="mt-1 font-medium">{"Male"}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">
                    Mobile Number
                  </Label>
                  <p className="mt-1 font-medium">{selectedUser?.mobile}</p>
                </div>
                <div className="md:col-span-2">
                  <Label className="text-xs text-muted-foreground">
                    Email Address
                  </Label>
                  <p className="mt-1 font-medium">{"-"}</p>
                </div>
              </div>
            </div>

            {/* Address Section */}
            <div className="border-b bg-muted/20 p-6">
              <div className="mb-5 flex items-center gap-2">
                <MapPin className="h-5 w-5 text-primary" />
                <h3 className="font-semibold text-secondary">
                  Address Details
                </h3>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">
                  Residential Address
                </Label>
                <p className="mt-1 font-medium">{"-"}</p>
              </div>
            </div>

            {/* KYC Documents Section */}
            <div className="border-b p-6">
              <div className="mb-5 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5 text-primary" />
                  <h3 className="font-semibold text-secondary">
                    KYC Documents
                  </h3>
                </div>
                {getKYCBadge(selectedUser.kycStatus)}
              </div>
              <div className="grid grid-cols-1 gap-x-8 gap-y-5 md:grid-cols-2 lg:grid-cols-4">
                <div>
                  <Label className="text-xs text-muted-foreground">
                    PAN Number
                  </Label>
                  <p className="mt-1 inline-block rounded bg-muted/50 px-3 py-1.5 font-mono font-medium">
                    {selectedUser?.pan}
                  </p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">
                    Aadhaar Number
                  </Label>
                  <p className="mt-1 inline-block rounded bg-muted/50 px-3 py-1.5 font-mono font-medium">
                    {"-"}
                  </p>
                </div>
              </div>
            </div>

            {/* Bank Details Section */}
            <div className="border-b bg-muted/20 p-6">
              <div className="mb-5 flex items-center gap-2">
                <Building2 className="h-5 w-5 text-primary" />
                <h3 className="font-semibold text-secondary">Bank Details</h3>
              </div>
              <div className="grid grid-cols-1 gap-x-8 gap-y-5 md:grid-cols-2 lg:grid-cols-4">
                <div>
                  <Label className="text-xs text-muted-foreground">
                    Bank Name
                  </Label>
                  <p className="mt-1 font-medium">{"-"}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">
                    Account Number
                  </Label>
                  <p className="mt-1 font-mono font-medium">{"-"}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">
                    IFSC Code
                  </Label>
                  <p className="mt-1 font-mono font-medium">{"-"}</p>
                </div>
              </div>
            </div>

            {/* Investment Preferences Section */}
            <div className="border-b p-6">
              <div className="mb-5 flex items-center gap-2">
                <Wallet className="h-5 w-5 text-primary" />
                <h3 className="font-semibold text-secondary">
                  Investment Preferences
                </h3>
              </div>
              <div className="grid grid-cols-1 gap-x-8 gap-y-5 md:grid-cols-2 lg:grid-cols-4">
                <div>
                  <Label className="text-xs text-muted-foreground">
                    PRAN Number
                  </Label>
                  <p className="mt-1 inline-block rounded border border-primary/20 bg-primary/5 px-3 py-1.5 font-mono font-medium">
                    {selectedUser?.pranNumber}
                  </p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">
                    Account Tier
                  </Label>
                  <div className="mt-1">
                    <Badge variant="outline">{"-"}</Badge>
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">
                    Risk Profile
                  </Label>
                  <div className="mt-1">{getRiskBadge("Medium")}</div>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">PFM</Label>
                  <p className="mt-1 font-medium">{"-"}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">
                    Contribution Frequency
                  </Label>
                  <p className="mt-1 font-medium">{"-"}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">
                    Contribution Amount
                  </Label>
                  <p className="mt-1 font-medium">₹{(0).toLocaleString()}</p>
                </div>
              </div>
            </div>

            {/* Nominee Details Section */}
            <div className="border-b bg-muted/20 p-6">
              <div className="mb-5 flex items-center gap-2">
                <UserCheck className="h-5 w-5 text-primary" />
                <h3 className="font-semibold text-secondary">
                  Nominee Details
                </h3>
              </div>
              <div className="grid grid-cols-1 gap-x-8 gap-y-5 md:grid-cols-2 lg:grid-cols-4">
                <div>
                  <Label className="text-xs text-muted-foreground">
                    Nominee Name
                  </Label>
                  <p className="mt-1 font-medium">{"-"}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">
                    Relationship
                  </Label>
                  <p className="mt-1 font-medium">{"-"}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">
                    Nominee DOB
                  </Label>
                  <p className="mt-1 font-medium">{"-"}</p>
                </div>
              </div>
            </div>

            {/* Account Summary Section */}
            <div className="p-6">
              <div className="mb-5 flex items-center gap-2">
                <ScrollText className="h-5 w-5 text-primary" />
                <h3 className="font-semibold text-secondary">
                  Account Summary
                </h3>
              </div>
              <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                <div>
                  <Label className="text-xs text-muted-foreground">
                    Member Since
                  </Label>
                  <p className="mt-1 text-lg font-bold">
                    {selectedUser?.created_on}
                  </p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">
                    Total Contributed
                  </Label>
                  <p className="mt-1 text-lg font-bold">
                    ₹{(0).toLocaleString()}
                  </p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">
                    Current Corpus
                  </Label>
                  <p className="mt-1 text-lg font-bold text-green-600">
                    ₹{(0).toLocaleString()}
                  </p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">
                    Total Gains
                  </Label>
                  <p className="mt-1 text-lg font-bold text-accent">
                    ₹{(0).toLocaleString()}
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* KYC Action Card */}

        <Card className="border-amber-200 bg-amber-50/50">
          <CardContent className="p-6">
            <div className="flex flex-col items-center justify-between gap-4 md:flex-row">
              <div className="flex items-center gap-3">
                <AlertTriangle className="h-6 w-6 text-amber-600" />
                <div>
                  <p className="font-semibold text-secondary">
                    KYC Verification Required
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Review the documents and take action
                  </p>
                </div>
              </div>
              <div className="flex gap-3">
                <Button
                  onClick={() => handleApproveKYC(selectedUser?.id)}
                  className="gap-2 bg-green-600 hover:bg-green-700"
                >
                  <CheckCircle className="h-4 w-4" />
                  Approve
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => handleRejectKYC(selectedUser?.id)}
                  className="gap-2"
                >
                  <XCircle className="h-4 w-4" />
                  Reject
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const columns: TableColumnsType<SubmissionResult> = [
    {
      title: "User Details",
      key: "userDetails",
      render: (_, record) => (
        <div>
          <p className="text-sm font-medium text-secondary">
            {record.subFstName}
          </p>
          <p className="text-xs text-muted-foreground">{record.mobile}</p>
        </div>
      ),
    },
    {
      title: "PRAN",
      key: "pran",
      render: (_, record) => (
        <div>
          <p className="font-mono text-sm text-secondary">
            {record.pranNumber}
          </p>
          <p className="text-xs text-muted-foreground">
            Since {record.created_on}
          </p>
        </div>
      ),
    },
    {
      title: "ACK ID",
      dataIndex: "ackId",
      key: "ackId",
    },
    {
      title: "Receipt Number",
      dataIndex: "receiptNumber",
      key: "receiptNumber",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: status => getStatusBadge(status),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <div className="flex items-center justify-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => handleViewUser(record)}
          >
            <Eye className="h-4 w-4 text-primary" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => {
              setSelectedUser(record);
              setShowEditDialog(true);
            }}
          >
            <Edit2 className="h-4 w-4 text-blue-600" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => handleDelete(record.id as any)}
          >
            <Trash2 className="h-4 w-4 text-red-500" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="min-h-screen space-y-6 bg-gray-50 p-6">
      {/* Header */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-secondary via-secondary/95 to-primary/80 p-6 md:p-8">
        <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent" />
        <svg
          className="absolute right-0 top-0 h-full w-1/3 text-white/5"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
        >
          <polygon fill="currentColor" points="50,0 100,0 100,100 0,100" />
        </svg>
        <div className="relative flex flex-col justify-between gap-4 md:flex-row md:items-center">
          <div className="flex items-center gap-4">
            <div className="rounded-xl bg-white/10 p-3 backdrop-blur-sm">
              <TrendingUp className="h-7 w-7 text-white" />
            </div>
            <div>
              <h1 className="font-display text-2xl font-bold text-white md:text-3xl">
                NPS User Management
              </h1>
              <p className="mt-1 text-white/70">
                Manage PRAN accounts, KYC verification, and user portfolios
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button
              variant="outline"
              className="gap-2 border-white/20 bg-white/10 text-white hover:bg-white/20"
              onClick={handleExport}
              disabled={selectedRows.length === 0}
            >
              <Download className="h-4 w-4" />
              Export
              {selectedRows.length > 0 && (
                <span className="ml-1 rounded-full bg-white/25 px-1.5 py-0.5 text-[11px] font-semibold leading-none">
                  {selectedRows.length}
                </span>
              )}
            </Button>
            <Button
              variant="outline"
              className="gap-2 border-white/20 bg-white/10 text-white hover:bg-white/20"
            >
              <UserPlus className="h-4 w-4" />
              Add New User
            </Button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-6">
        <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-primary/10">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-xl bg-primary/10 p-2.5">
                <Users className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold text-secondary">
                  {stats.totalUsers}
                </p>
                <p className="text-xs text-muted-foreground">Total Users</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-xl bg-green-100 p-2.5">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-secondary">
                  {stats.activeUsers}
                </p>
                <p className="text-xs text-muted-foreground">Active</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-xl bg-amber-100 p-2.5">
                <Clock className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-secondary">
                  {stats.pendingUsers}
                </p>
                <p className="text-xs text-muted-foreground">Pending KYC</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-xl bg-blue-100 p-2.5">
                <IndianRupee className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-secondary">
                  ₹{(stats.totalAUM / 100000).toFixed(1)}L
                </p>
                <p className="text-xs text-muted-foreground">Total AUM</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-xl bg-purple-100 p-2.5">
                <TrendingUp className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-secondary">
                  ₹{(stats.totalContributions / 100000).toFixed(1)}L
                </p>
                <p className="text-xs text-muted-foreground">Contributions</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-green-200 bg-gradient-to-br from-green-50 to-green-100/50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-xl bg-green-100 p-2.5">
                <TrendingUp className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-green-600">
                  +{stats.avgReturns.toFixed(1)}%
                </p>
                <p className="text-xs text-muted-foreground">Avg Returns</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col gap-4 md:flex-row">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search by name, email, mobile, or PRAN..."
                className="pl-10"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
            </div>
            <Tabs value={statusFilter} onValueChange={setStatusFilter}>
              <TabsList className="bg-muted/50">
                <TabsTrigger value="all">All Users</TabsTrigger>
                <TabsTrigger value="active">Active</TabsTrigger>
                <TabsTrigger value="pending">Pending KYC</TabsTrigger>
                <TabsTrigger value="inactive">Inactive</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card>
        <AntDTable
          showSettings
          data={(data?.results as unknown as SubmissionResult[]) ?? []}
          columns={columns}
          rowKey="id"
          rowSelection={{
            selectedRowKeys,
            onChange: onSelectChange,
          }}
          loading={isLoading}
          pagination={{
            pageSize: pageSize,
            current: page,
            total: data?.count ?? 0,
            onChange: (page, size) => {
              setPage(page);
              setPageSize(size);
            },
            showSizeChanger: true,
            showTotal: (total, range) =>
              `Showing ${range[0]}-${range[1]} of ${total} users`,
            pageSizeOptions: ["5", "10", "20", "50"],
          }}
          scroll={{ x: 1200 }}
          emptyText="No users found"
        />
      </Card>
    </div>
  );
};

export default AdminNPSUsers;
